/**
 * InteractionDetailCard - geteilte Darstellung einer Substanz-Interaktion.
 * Wird sowohl auf der oeffentlichen Detailseite /matrix/$pair als auch im
 * Admin-Panel als Live-Vorschau gerendert. Keine DB-Aufrufe - nur Props.
 */
import { useState } from "react";
import { AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { riskMeta, MARKER_META, type InteractionContent, type InteractionRisk, type Evidenz } from "@/types/restlicht-db";
import { riskLabelKey } from "@/lib/risk-labels";
import { toneAlpha } from "@/lib/tone";
import { useT } from "@/i18n/LanguageContext";
import { useSourceCitations } from "@/hooks/useSourceCitations";
import { SourceListItem } from "@/components/SourceListItem";

interface Props {
  aName: string;
  bName: string;
  riskLevel: InteractionRisk;
  markers?: string[] | null;
  content: InteractionContent | null;
  variant?: "page" | "preview";
  /** Enables clickable [n] citations backed by the interaction's linked sources. */
  interactionId?: number | null;
}

const EVIDENZ_STYLE: Record<Evidenz, { bg: string; border: string; fg: string; label: string }> = {
  gesichert: { bg: "#0d3b2e", border: "#10b98166", fg: "#34d399", label: "gesichert" },
  wahrscheinlich: { bg: "#3b2a0d", border: "#f59e0b66", fg: "#fbbf24", label: "wahrscheinlich" },
  unzureichend: { bg: "#2a2a2a", border: "#9ca3af55", fg: "#d1d5db", label: "unzureichend" },
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-muted)", fontWeight: 600 }}>{title}</div>
      <div style={{ fontSize: "0.88rem", color: "var(--text-primary)", lineHeight: 1.55 }}>{children}</div>
    </div>
  );
}

function EvidenzBadge({ evidenz }: { evidenz: Evidenz }) {
  const s = EVIDENZ_STYLE[evidenz];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "3px 9px", borderRadius: 999,
      background: s.bg, border: `1px solid ${s.border}`, color: s.fg,
      fontSize: "0.72rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em",
    }}>
      Evidenz: {s.label}
    </span>
  );
}

export function InteractionDetailCard({ aName, bName, riskLevel, markers, content, variant = "page", interactionId = null }: Props) {
  const t = useT();
  const meta = riskMeta(riskLevel);
  const tone = meta.tone;
  const riskLabel = t(riskLabelKey(riskLevel));
  const [open, setOpen] = useState(variant === "page");
  // content is always content_de here, so source notes are read in German too.
  const { numbered, domPrefix, highlighted, cite } = useSourceCitations(interactionId, "de", content?.quellen, () => setOpen(true));

  return (
    <div style={{
      padding: variant === "page" ? "1.5rem" : "1rem",
      borderRadius: 12,
      background: toneAlpha(tone, 6),
      border: `1px solid ${toneAlpha(tone, 33)}`,
      display: "flex", flexDirection: "column", gap: "1rem",
    }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ fontSize: variant === "page" ? "1.4rem" : "1.05rem", fontWeight: 700, color: "var(--text-primary)" }}>
          {aName || "-"} <span style={{ color: "var(--text-muted)" }}>+</span> {bName || "-"}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
          <span
            role="img"
            aria-label={`Risiko: ${riskLabel}`}
            title={riskLabel}
            data-testid="detail-risk-label"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "0.35rem 0.85rem", borderRadius: 20,
              background: toneAlpha(tone, 15), color: tone, border: `1px solid ${toneAlpha(tone, 53)}`,
              fontSize: "0.8rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em",
            }}
          >
            <AlertTriangle size={14} aria-hidden="true" /> {riskLabel}
          </span>
          {markers?.map((m) => {
            const md = MARKER_META[m];
            const aria = md ? `${md.label}: ${md.description}` : m;
            return (
              <span
                key={m}
                role="img"
                aria-label={aria}
                title={aria}
                style={{
                  padding: "0.25rem 0.6rem", borderRadius: 12,
                  background: md ? toneAlpha(md.tone, 13) : "rgba(255,255,255,0.05)",
                  border: `1px solid ${md ? toneAlpha(md.tone, 53) : "rgba(255,255,255,0.15)"}`,
                  fontSize: "0.7rem",
                  color: md ? md.tone : "var(--text-secondary, var(--text-primary))",
                  textTransform: "uppercase", letterSpacing: "0.04em",
                  cursor: "help",
                }}
              >
                {md?.label ?? m}
              </span>
            );
          })}
        </div>
      </div>


      {!content ? (
        <div style={{ fontSize: "0.85rem", color: "var(--text-muted)", fontStyle: "italic" }}>
          Noch keine ausführlichen Inhalte hinterlegt.
        </div>
      ) : (
        <>
          <EvidenzBadge evidenz={content.evidenz} />

          {content.kernaussage && (
            <div style={{
              padding: "0.8rem 1rem", borderRadius: 8,
              background: toneAlpha(tone, 8), border: `1px solid ${toneAlpha(tone, 33)}`,
              fontSize: "0.95rem", fontWeight: 600, color: "var(--text-primary)", lineHeight: 1.5,
            }}>
              {cite(content.kernaussage)}
            </div>
          )}

          {content.was_passiert && <Section title="Was passiert">{cite(content.was_passiert)}</Section>}
          {content.fuer_wen && <Section title="Für wen relevant">{cite(content.fuer_wen)}</Section>}

          {(content.warnzeichen?.length > 0 || content.warnzeichen_hinweis) && (
            <div style={{
              padding: "0.85rem 0.95rem", borderRadius: 10,
              background: "rgba(220, 38, 38, 0.10)",
              border: "1.5px solid rgba(220, 38, 38, 0.55)",
              display: "flex", flexDirection: "column", gap: 8,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#fca5a5", fontWeight: 700, fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                <AlertTriangle size={16} /> Warnzeichen
              </div>
              {content.warnzeichen?.length > 0 && (
                <ul style={{ margin: 0, paddingLeft: "1.1rem", fontSize: "0.85rem", color: "var(--text-primary)", lineHeight: 1.5 }}>
                  {content.warnzeichen.map((w, i) => <li key={i}>{cite(w)}</li>)}
                </ul>
              )}
              {content.warnzeichen_hinweis && (
                <div style={{ fontSize: "0.8rem", color: "var(--text-secondary, var(--text-primary))" }}>
                  {cite(content.warnzeichen_hinweis)}
                </div>
              )}
            </div>
          )}

          {content.wenn_passiert?.length > 0 && (
            <Section title="Wenn es passiert">
              <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
                {content.wenn_passiert.map((w, i) => <li key={i}>{cite(w)}</li>)}
              </ul>
            </Section>
          )}

          {(content.experte?.length > 0 || content.quellen?.length > 0 || numbered.length > 0) && (
            <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "0.8rem" }}>
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%",
                  background: "transparent", border: "none", color: "var(--text-primary)",
                  cursor: "pointer", padding: "0.4rem 0", fontSize: "0.85rem", fontWeight: 600, textAlign: "left",
                }}
              >
                <span>Kenner-Bereich (Experte &amp; Quellen)</span>
                {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {open && (
                <div style={{ display: "flex", flexDirection: "column", gap: "0.7rem", marginTop: "0.6rem" }}>
                  {content.experte?.map((p, i) => (
                    <p key={i} style={{ margin: 0, fontSize: "0.83rem", color: "var(--text-secondary, var(--text-primary))", lineHeight: 1.6 }}>{cite(p)}</p>
                  ))}
                  {(content.quellen?.length > 0 || numbered.length > 0) && (
                    <Section title="Quellen">
                      <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6, wordBreak: "break-word" }}>
                        {numbered.map((e) => (
                          <SourceListItem key={e.key} entry={e} domId={`${domPrefix}-${e.n}`} highlighted={highlighted === e.n} cite={cite} />
                        ))}
                      </ul>
                    </Section>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
